import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import type { FevCandidata } from '@/lib/supabase/tipos-bd'
import { mensajeDeError } from '@/lib/supabase/errores'
import { fecha, pesos } from '@/shared/utilidades/formato'

import estilos from './PaginaCartera.module.css'

/**
 * «¿YA SE FACTURÓ EN WORLD OFFICE?» (0257).
 *
 * Johan: «hay COT que ya pasaron a FEV, pero las pasaron en WO». La app
 * reconoce sola las que repiten los productos de la cotización; esto es para
 * las que no —se facturó otro producto, se juntaron dos—. Lista las FEV del
 * cliente desde la fecha de la cotización que nadie más tiene, con cuánto se
 * parecen, y quien lleva la cartera escoge la que es.
 */
export function YaEnUnaFev({ cotizacionId }: { cotizacionId: string }) {
  const [abierto, setAbierto] = useState(false)
  const cliente = useQueryClient()

  const candidatas = useQuery({
    queryKey: ['fev-candidatas', cotizacionId],
    enabled: abierto,
    queryFn: async (): Promise<FevCandidata[]> => {
      const { data, error } = await supabase.rpc('fn_fev_candidatas', {
        p_cotizacion_id: cotizacionId,
      })
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as FevCandidata[]
    },
  })

  const marcar = useMutation({
    mutationFn: async (asiento: number) => {
      const { error } = await supabase.rpc('fn_marcar_cot_facturada', {
        p_cotizacion_id: cotizacionId,
        p_wo_id_asiento_factura: asiento,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cartera'] })
      await cliente.invalidateQueries({ queryKey: ['alistamientos'] })
    },
  })

  if (!abierto) {
    return (
      <button type="button" className={estilos.yaFevAbrir} onClick={() => setAbierto(true)}>
        ¿Ya se facturó en World Office? Escoger la FEV ›
      </button>
    )
  }

  const lista = candidatas.data ?? []

  return (
    <div className={estilos.yaFev}>
      <p className={estilos.yaFevTitulo}>
        FEV del cliente desde la cotización
        <button type="button" onClick={() => setAbierto(false)} aria-label="Cerrar">
          ×
        </button>
      </p>
      {candidatas.isLoading ? (
        <p className={estilos.yaFevVacio}>Buscando en World Office…</p>
      ) : candidatas.error ? (
        <p className={estilos.yaFevError}>{(candidatas.error as Error).message}</p>
      ) : lista.length === 0 ? (
        <p className={estilos.yaFevVacio}>
          No hay FEV del cliente libres desde la fecha de la cotización. Si se facturó, puede que
          todavía no haya llegado de World Office.
        </p>
      ) : (
        <ul>
          {lista.map((f) => (
            <li key={f.wo_id_asiento}>
              <span className={estilos.yaFevDatos}>
                <span>
                  <b>{f.numero}</b> · {fecha(f.fecha)} · {pesos(Number(f.total))}
                </span>
                <small>
                  {f.coinciden} de {f.renglones_cot} productos iguales
                  {f.nombra_la_orden && ' · nombra la orden'}
                  {f.nota && ` · «${f.nota.slice(0, 60)}»`}
                </small>
              </span>
              <button
                type="button"
                className={estilos.yaFevEsta}
                onClick={() => marcar.mutate(f.wo_id_asiento)}
                disabled={marcar.isPending}
              >
                Es esta
              </button>
            </li>
          ))}
        </ul>
      )}
      {marcar.error && <p className={estilos.yaFevError}>{marcar.error.message}</p>}
    </div>
  )
}
